<template>
  <div class="space-y-5">
    <!-- 标题 -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h2 class="text-lg font-bold text-neutral-900">我的知识链</h2>
        <p class="text-xs text-neutral-400 mt-0.5">
          以知识点为节点，把知识体系和错题连成一张图：错得越多，节点越红
        </p>
      </div>
      <router-link
        to="/knowledge-link"
        class="text-sm text-primary hover:underline font-medium"
      >
        <i class="fa fa-pencil mr-1"></i>维护知识体系
      </router-link>
    </div>

    <!-- 学科 -->
    <div class="flex gap-2 overflow-x-auto pb-1">
      <button
        v-for="s in subjects"
        :key="s"
        @click="switchSubject(s)"
        class="px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors"
        :class="
          subject === s
            ? 'bg-primary text-white font-medium'
            : 'bg-white text-neutral-600 border border-neutral-200 hover:border-primary hover:text-primary'
        "
      >
        {{ s }}
      </button>
    </div>

    <div v-if="loading" class="py-12 text-center text-neutral-400">
      <i class="fa fa-circle-o-notch fa-spin text-xl"></i>
    </div>

    <template v-else>
      <!-- 统计 -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div class="bg-white rounded-xl border border-neutral-200 p-4">
          <p class="text-2xl font-bold text-neutral-900">{{ stats.points || 0 }}</p>
          <p class="text-xs text-neutral-400 mt-0.5">知识点</p>
        </div>
        <div class="bg-white rounded-xl border border-neutral-200 p-4">
          <p class="text-2xl font-bold text-neutral-900">{{ stats.wrongs || 0 }}</p>
          <p class="text-xs text-neutral-400 mt-0.5">错题总数</p>
        </div>
        <div class="bg-white rounded-xl border border-neutral-200 p-4">
          <p class="text-2xl font-bold text-rose-500">{{ stats.weak || 0 }}</p>
          <p class="text-xs text-neutral-400 mt-0.5">薄弱知识点</p>
        </div>
        <div class="bg-white rounded-xl border border-neutral-200 p-4">
          <p class="text-2xl font-bold text-amber-500">{{ stats.unmatched || 0 }}</p>
          <p class="text-xs text-neutral-400 mt-0.5">未归入体系</p>
        </div>
      </div>

      <!-- 知识图谱 -->
      <div v-if="chapters.length" class="bg-white rounded-xl border border-neutral-200 p-4">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-bold text-neutral-900">
            <i class="fa fa-share-alt text-primary mr-1"></i>知识图谱
          </h3>
          <div class="flex items-center gap-3 text-[11px] text-neutral-500">
            <span><i class="fa fa-circle text-emerald-500 mr-1"></i>掌握好</span>
            <span><i class="fa fa-circle text-amber-500 mr-1"></i>需巩固</span>
            <span><i class="fa fa-circle text-rose-500 mr-1"></i>薄弱</span>
          </div>
        </div>
        <svg :viewBox="`0 0 800 ${svgHeight}`" class="w-full h-auto select-none">
          <!-- 连线 -->
          <line
            v-for="(l, i) in graph.links"
            :key="'l' + i"
            :x1="l.x1"
            :y1="l.y1"
            :x2="l.x2"
            :y2="l.y2"
            :stroke="l.strong ? '#cbd5e1' : '#e5e7eb'"
            :stroke-width="l.strong ? 1.6 : 1"
          />
          <!-- 节点 -->
          <g
            v-for="n in graph.nodes"
            :key="n.id"
            class="cursor-pointer"
            @click="n.type === 'point' && selectPoint(n.raw)"
          >
            <circle
              :cx="n.x"
              :cy="n.y"
              :r="n.r"
              :fill="n.fill"
              :stroke="selectedId === n.id ? '#111827' : '#ffffff'"
              :stroke-width="selectedId === n.id ? 3 : 2"
              :opacity="n.type === 'point' && n.raw.wrongCount === 0 ? 0.55 : 1"
            />
            <text
              v-if="n.type === 'center'"
              :x="n.x"
              :y="n.y + 4"
              text-anchor="middle"
              font-size="14"
              fill="#ffffff"
              font-weight="700"
            >
              {{ n.label }}
            </text>
            <text
              v-else
              :x="n.x"
              :y="n.y - n.r - 5"
              text-anchor="middle"
              font-size="11"
              :fill="n.type === 'chapter' ? '#374151' : '#6b7280'"
              :font-weight="n.type === 'chapter' ? 700 : 400"
            >
              {{ n.label }}
            </text>
          </g>
        </svg>
        <p class="text-[11px] text-neutral-400 mt-1">
          点的颜色代表掌握度（错题越多越红），无错题的点为浅色。点一下知识点可查看它的错题。
        </p>
      </div>

      <!-- 空态 -->
      <div
        v-if="!chapters.length"
        class="bg-white rounded-xl border border-neutral-200 p-12 text-center"
      >
        <div
          class="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400"
        >
          <i class="fa fa-sitemap text-2xl"></i>
        </div>
        <h3 class="text-lg font-medium text-neutral-900 mb-2">
          {{ subject }}还没有知识体系
        </h3>
        <p class="text-neutral-500 text-sm mb-6">
          先到「知识链接」按章节建知识点，错题就会自动挂到对应知识点上。
        </p>
        <router-link
          to="/knowledge-link"
          class="inline-block px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
        >
          去建知识体系
        </router-link>
      </div>

      <!-- 章节 → 知识点 → 错题 -->
      <div v-else class="space-y-4">
        <div
          v-for="c in chapters"
          :key="c.id"
          class="bg-white rounded-xl border border-neutral-200 overflow-hidden"
        >
          <div
            class="flex items-center gap-3 p-4 cursor-pointer hover:bg-neutral-50"
            @click="toggleChapter(c.id)"
          >
            <i
              class="fa text-neutral-400 text-xs"
              :class="openChapter[c.id] ? 'fa-chevron-down' : 'fa-chevron-right'"
            ></i>
            <span class="font-bold text-neutral-900 flex-1 truncate">{{ c.title }}</span>
            <span class="text-[11px] text-neutral-400">
              {{ (c.points || []).length }} 个知识点 ·
              {{ chapterWrongCount(c) }} 道错题
            </span>
          </div>

          <div v-if="openChapter[c.id]" class="border-t border-neutral-100">
            <div
              v-for="p in c.points"
              :key="p.id"
              class="border-b border-neutral-50 last:border-0"
            >
              <div
                class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-neutral-50"
                @click="selectPoint(p)"
              >
                <span
                  class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  :style="{ background: masteryColor(p.mastery) }"
                ></span>
                <span class="text-sm text-neutral-800 flex-1 truncate">{{ p.title }}</span>

                <!-- 掌握度条 -->
                <div class="hidden sm:block w-24 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                  <div
                    class="h-full rounded-full"
                    :style="{
                      width: p.mastery + '%',
                      background: masteryColor(p.mastery),
                    }"
                  ></div>
                </div>

                <span
                  class="text-[11px] px-2 py-0.5 rounded-full flex-shrink-0"
                  :class="
                    p.wrongCount > 0
                      ? 'bg-rose-50 text-rose-500'
                      : 'bg-neutral-100 text-neutral-400'
                  "
                >
                  {{ p.wrongCount }} 错题
                </span>
              </div>

              <!-- 针对性错题 -->
              <div v-if="selectedId === p.id" class="px-4 pb-4">
                <div v-if="!p.wrongs || !p.wrongs.length" class="text-xs text-neutral-400 py-2">
                  这个知识点还没有错题，保持得不错。
                </div>
                <div v-else class="space-y-2">
                  <div
                    v-for="w in p.wrongs"
                    :key="w.id"
                    class="bg-neutral-50 rounded-lg p-3 border border-neutral-100"
                  >
                    <p class="text-sm text-neutral-800 leading-relaxed">
                      {{ w.content || "（无题干）" }}
                    </p>
                    <p v-if="w.analysis" class="text-xs text-neutral-500 mt-1">
                      <i class="fa fa-lightbulb-o text-amber-500 mr-1"></i>{{ w.analysis }}
                    </p>
                  </div>
                  <router-link
                    to="/wrongbook"
                    class="inline-block text-xs text-primary hover:underline"
                  >
                    去错题本看完整分析 →
                  </router-link>
                </div>
              </div>
            </div>

            <div v-if="!(c.points || []).length" class="px-4 py-4 text-xs text-neutral-400">
              这个章节下还没有知识点
            </div>
          </div>
        </div>

        <!-- 未归入体系 -->
        <div
          v-if="data.unmatched && data.unmatched.length"
          class="bg-white rounded-xl border border-amber-200 overflow-hidden"
        >
          <div
            class="flex items-center gap-3 p-4 cursor-pointer hover:bg-amber-50/50"
            @click="showUnmatched = !showUnmatched"
          >
            <i
              class="fa text-amber-500 text-xs"
              :class="showUnmatched ? 'fa-chevron-down' : 'fa-chevron-right'"
            ></i>
            <span class="font-bold text-neutral-900 flex-1">未归入知识体系的错题</span>
            <span class="text-[11px] text-neutral-400">{{ data.unmatched.length }} 道</span>
          </div>
          <div v-if="showUnmatched" class="border-t border-neutral-100 p-4 space-y-2">
            <p class="text-xs text-neutral-400 mb-2">
              这些错题的知识点名称在知识体系里找不到，可以到「知识链接」补上对应知识点。
            </p>
            <div
              v-for="w in data.unmatched"
              :key="w.id"
              class="bg-neutral-50 rounded-lg p-3 border border-neutral-100"
            >
              <p class="text-sm text-neutral-800">{{ w.content || "（无题干）" }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "KnowledgeChainPanel",
  data() {
    return {
      subjects: ["语文", "数学", "英语", "物理", "化学", "生物", "政治", "历史", "地理", "科学"],
      subject: "语文",
      loading: false,
      data: { chapters: [], unmatched: [], stats: {} },
      openChapter: {},
      showUnmatched: false,
      selectedId: "",
    };
  },
  computed: {
    chapters() {
      return (this.data && this.data.chapters) || [];
    },
    stats() {
      const s = (this.data && this.data.stats) || {};
      return {
        points: s.points || 0,
        wrongs: s.wrongs || 0,
        weak: s.weak || 0,
        unmatched: s.unmatched || 0,
      };
    },
    svgHeight() {
      return 520;
    },
    /** 放射状布局：中心=学科，内环=章节，外环=知识点 */
    graph() {
      const chapters = this.chapters;
      const cx = 400;
      const cy = 260;
      const nodes = [
        {
          id: "__center",
          x: cx,
          y: cy,
          r: 34,
          label: this.subject,
          type: "center",
          fill: "#6366f1",
        },
      ];
      const links = [];
      const n = chapters.length;
      if (!n) return { nodes, links };

      chapters.forEach((c, i) => {
        const a = (Math.PI * 2 * i) / n - Math.PI / 2;
        const x = cx + Math.cos(a) * 135;
        const y = cy + Math.sin(a) * 135;
        nodes.push({
          id: c.id,
          x,
          y,
          r: 20,
          label: this.clip(c.title, 6),
          type: "chapter",
          fill: "#e0e7ff",
          raw: c,
        });
        links.push({ x1: cx, y1: cy, x2: x, y2: y, strong: true });

        const pts = c.points || [];
        const m = pts.length;
        if (!m) return;
        const spread = Math.min(Math.PI * 0.9, m * 0.32);
        pts.forEach((p, j) => {
          const ang = a + (m > 1 ? (j / (m - 1) - 0.5) * spread : 0);
          const px = cx + Math.cos(ang) * 225;
          const py = cy + Math.sin(ang) * 225;
          nodes.push({
            id: p.id,
            x: px,
            y: py,
            r: 9,
            label: this.clip(p.title, 7),
            type: "point",
            fill: this.masteryColor(p.mastery),
            raw: p,
          });
          links.push({ x1: x, y1: y, x2: px, y2: py, strong: false });
        });
      });
      return { nodes, links };
    },
  },
  async mounted() {
    await this.loadSubjects();
    this.load();
  },
  methods: {
    /** 学科以后台「学科分类」为准 */
    async loadSubjects() {
      try {
        const res = await this.$axios({ url: "subinfo/list", method: "GET" });
        const list = (res && res.data) || [];
        const names = (Array.isArray(list) ? list : [])
          .map((x) => (typeof x === "string" ? x : x.title))
          .filter(Boolean);
        if (names.length) {
          this.subjects = names;
          if (!names.includes(this.subject)) {
            this.subject = names.includes("语文") ? "语文" : names[0];
          }
        }
      } catch (e) {
        /* 保留默认学科 */
      }
    },
    clip(text, max) {
      const t = String(text || "");
      return t.length > max ? t.slice(0, max) + "…" : t;
    },
    masteryColor(m) {
      const v = typeof m === "number" ? m : 100;
      if (v >= 80) return "#10b981";
      if (v >= 50) return "#f59e0b";
      return "#f43f5e";
    },
    chapterWrongCount(c) {
      return (c.points || []).reduce((sum, p) => sum + (p.wrongCount || 0), 0);
    },
    async load() {
      if (!localStorage.getItem("token")) return;
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "knowledge-link/graph",
          method: "GET",
          params: { subject: this.subject },
        });
        if (res.code === 200 && res.data) {
          this.data = res.data;
          const first = (res.data.chapters || [])[0];
          if (first) this.openChapter[first.id] = true;
        }
      } catch (e) {
        /* ignore */
      }
      this.loading = false;
    },
    switchSubject(s) {
      this.subject = s;
      this.selectedId = "";
      this.openChapter = {};
      this.load();
    },
    toggleChapter(id) {
      this.openChapter[id] = !this.openChapter[id];
    },
    selectPoint(p) {
      if (!p) return;
      this.selectedId = this.selectedId === p.id ? "" : p.id;
      // 同步展开所属章节
      const c = this.chapters.find((x) => (x.points || []).some((y) => y.id === p.id));
      if (c) this.openChapter[c.id] = true;
    },
  },
};
</script>
